<script setup>
import { useGlobalStore } from "@stores/global";
import { useTriviaStore } from "@stores/trivia";
import Expedia from "@components/logos/Expedia.vue";
import Speed from "@components/logos/Speed.vue";
import IconX from "@components/icons/X.vue";
import IconMenu from "@components/icons/Menu.vue";
import IconArrowLeft from "@components/icons/ArrowLeft.vue";
import { navLinks } from "../utils/navLinks";

const globalStore = useGlobalStore();
const triviaStore = useTriviaStore();

function needsTriviaConfirm() {
    return (
        globalStore.activePage === "trivia" && triviaStore.gameState !== "intro"
    );
}

function navigate(link) {
    const prev = globalStore.activePage;

    if (link.name === "trivia") {
        if (needsTriviaConfirm()) {
            globalStore.openModal("leaveTriviaConfirm", { payload: "trivia" });
            return;
        }
        globalStore.setPage("trivia");
        globalStore.showPanel("trivia");
        triviaStore.reset();
    } else {
        if (prev === "trivia" && needsTriviaConfirm()) {
            globalStore.openModal("leaveTriviaConfirm", {
                payload: link.name,
            });
            return;
        }
        if (prev === "trivia") {
            globalStore.hidePanel("trivia");
            triviaStore.reset();
        }
        globalStore.setPage(link.name);
    }
}

function handleBack() {
    if (globalStore.activePage === "explore") {
        globalStore.exitToLanding();
        return;
    }
    if (needsTriviaConfirm()) {
        globalStore.openModal("leaveTriviaConfirm", { payload: "explore" });
        return;
    }
    globalStore.hidePanel("trivia");
    triviaStore.reset();
    globalStore.setPage("explore");
}
</script>

<template>
    <header class="header bg-marine-800 border-marine-500 border-b">
        <nav
            class="header--nav flex grid-cols-[repeat(var(--cols),minmax(0,1fr))]"
        >
            <button
                class="body-xs border-marine-500 text-marine-800 relative isolate flex min-w-[calc(var(--cell-x)*2)] items-center justify-center gap-2 border-r bg-yellow-400 text-center font-bold uppercase"
                @click="handleBack()"
            >
                <IconArrowLeft class="size-4" />
                <span>Back</span>
            </button>
            <button
                v-for="link in navLinks"
                :key="link.name"
                :class="[
                    'body-xs border-marine-500 before:from-marine-600 relative isolate flex min-w-[calc(var(--cell-x)*2)] items-center justify-center gap-2 border-r text-center font-bold uppercase transition-colors before:absolute before:inset-0 before:-z-1 before:bg-linear-to-t before:to-transparent before:opacity-0 before:transition-opacity',
                    globalStore.activePage === link.name
                        ? 'text-yellow-400 before:opacity-100'
                        : 'before:opacity-0 hover:before:opacity-50',
                ]"
                @click="navigate(link)"
            >
                <component :is="link.icon" class="size-4" />
                {{ link.label }}
            </button>
        </nav>

        <div class="header--nav-brand flex items-center justify-center gap-4">
            <Expedia class="h-5 w-auto lg:h-6" />
            <IconX class="size-5 text-yellow-400 lg:size-6" />
            <Speed class="h-5 w-auto lg:h-6" />
        </div>

        <button
            class="header--burger border-marine-500 items-center justify-center border-l text-white"
            @click="globalStore.toggleMenu()"
        >
            <IconX v-if="globalStore.menuOpen" class="size-6 text-yellow-400" />
            <IconMenu v-else class="size-6 text-yellow-400" />
        </button>
    </header>
</template>

<style scoped>
header {
    grid-column: 1 / -1;
    grid-row: 1 / span 1;
    z-index: 2;

    display: grid;
    grid-template-columns: subgrid;
    align-items: stretch;
}

.header--nav-brand {
    grid-column: -7 / -1;
}

.header--burger {
    display: none;
}

@media (max-width: 1326px) {
    .header--nav {
        display: none;
    }

    .header--nav-brand {
        grid-column: 1 / -2;
        justify-content: flex-start;
        padding-left: 12px;
    }

    .header--burger {
        display: flex;
        grid-column: -2 / -1;
    }
}
</style>
